import { createSlice } from "@reduxjs/toolkit";
import axios from "../../utils/axios";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const initialState = {
  isLoading: false,
  isSaving: false,
  error: null,
  warehouses: [],
  warehouse: null,
  page: 1,
  limit: 10,
  totalPages: 0,
  totalResults: 0,
};

function getErrorMessage(error, fallback) {
  if (typeof error === "string") {
    return error;
  }

  return error?.message || error?.error || fallback;
}

function normalizeWarehouse(warehouse) {
  if (!warehouse || typeof warehouse !== "object") {
    return warehouse;
  }

  const user = warehouse.user || warehouse.owner || {};

  return {
    ...warehouse,
    ownerFirstName:
      warehouse.ownerFirstName || warehouse.firstName || user.firstName || "",
    ownerLastName:
      warehouse.ownerLastName || warehouse.lastName || user.lastName || "",
    ownerEmail: warehouse.ownerEmail || warehouse.email || user.email || "",
    ownerPhoneNumber:
      warehouse.ownerPhoneNumber ||
      warehouse.phone ||
      warehouse.phoneNumber ||
      user.phoneNumber ||
      "",
    userName: warehouse.userName || user.userName || "",
    imageURL: warehouse.imageURL || warehouse.photoURL || "",
  };
}

function normalizeListResponse(data, fallbackPage, fallbackLimit) {
  const payload = data?.data || data;
  const resultPayload = payload?.result;
  const results =
    payload?.results ||
    payload?.warehouses ||
    resultPayload?.results ||
    resultPayload?.warehouses ||
    resultPayload ||
    [];
  const warehouses = Array.isArray(results)
    ? results.map(normalizeWarehouse)
    : [];
  const pagination =
    resultPayload && !Array.isArray(resultPayload) ? resultPayload : payload;

  return {
    warehouses,
    page: pagination?.page || fallbackPage,
    limit: pagination?.limit || fallbackLimit,
    totalPages:
      pagination?.totalPages ||
      Math.ceil(
        (pagination?.totalResults || warehouses.length) / fallbackLimit
      ),
    totalResults:
      pagination?.totalResults ?? pagination?.total ?? warehouses.length,
  };
}

function normalizeDetailResponse(data) {
  const warehouse =
    data?.data?.result ||
    data?.data?.warehouse ||
    data?.data ||
    data?.result ||
    data?.warehouse ||
    data;

  return normalizeWarehouse(warehouse);
}

const slice = createSlice({
  name: "franchiseWarehouse",
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
      state.error = null;
    },
    startSaving(state) {
      state.isSaving = true;
      state.error = null;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.isSaving = false;
      state.error = action.payload;
    },
    getWarehousesSuccess(state, action) {
      state.isLoading = false;
      state.warehouses = action.payload.warehouses;
      state.page = action.payload.page;
      state.limit = action.payload.limit;
      state.totalPages = action.payload.totalPages;
      state.totalResults = action.payload.totalResults;
    },
    getWarehouseSuccess(state, action) {
      state.isLoading = false;
      state.warehouse = action.payload;
    },
    saveWarehouseSuccess(state, action) {
      state.isSaving = false;
      state.warehouse = action.payload;
    },
    clearWarehouse(state) {
      state.warehouse = null;
      state.error = null;
    },
  },
});

export const { clearWarehouse } = slice.actions;

export default slice.reducer;

export function getFranchiseWarehouses({
  franchiseId,
  page = 1,
  limit = 10,
}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.get(
        `${backendUrl}/franchise/${franchiseId}/warehouses`,
        {
          params: { page, limit },
        }
      );

      dispatch(
        slice.actions.getWarehousesSuccess(
          normalizeListResponse(response.data, page, limit)
        )
      );
    } catch (error) {
      dispatch(
        slice.actions.hasError(
          getErrorMessage(error, "Unable to load franchise warehouses.")
        )
      );
    }
  };
}

export function getFranchiseWarehouse({ franchiseId, warehouseId }) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.get(
        `${backendUrl}/franchise/${franchiseId}/warehouses/${warehouseId}`
      );

      dispatch(
        slice.actions.getWarehouseSuccess(
          normalizeDetailResponse(response.data)
        )
      );
      return response.data;
    } catch (error) {
      dispatch(
        slice.actions.hasError(
          getErrorMessage(error, "Unable to load warehouse details.")
        )
      );
      throw error;
    }
  };
}

export function createFranchiseWarehouse({ franchiseId, payload }) {
  return async (dispatch) => {
    dispatch(slice.actions.startSaving());

    try {
      const response = await axios.post(
        `${backendUrl}/franchise/${franchiseId}/warehouses`,
        payload,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      dispatch(
        slice.actions.saveWarehouseSuccess(
          normalizeDetailResponse(response.data)
        )
      );
      return response.data;
    } catch (error) {
      dispatch(
        slice.actions.hasError(
          getErrorMessage(error, "Unable to create warehouse.")
        )
      );
      throw error;
    }
  };
}

export function updateFranchiseWarehouse({ franchiseId, warehouseId, payload }) {
  return async (dispatch) => {
    dispatch(slice.actions.startSaving());

    try {
      const response = await axios.patch(
        `${backendUrl}/franchise/${franchiseId}/warehouses/${warehouseId}`,
        payload,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      dispatch(
        slice.actions.saveWarehouseSuccess(
          normalizeDetailResponse(response.data)
        )
      );
      return response.data;
    } catch (error) {
      dispatch(
        slice.actions.hasError(
          getErrorMessage(error, "Unable to update warehouse.")
        )
      );
      throw error;
    }
  };
}
